/** Device-local invitation memory. Tokens never appear in diagnostics. */
export interface RememberedMissionReferral {
  referralId: string;
  token: string;
  branchId: string;
  periodMissionConfigurationId: string;
  expiresAt: string;
  openedAt: number;
  promotionDataUpdatedAt?: number;
  // A server-validated discovery snapshot, not an acceptance authorization.
  listing?: Record<string, any>;
}

interface Storage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<unknown>;
}

export const referralStorageKey = (backend: string, account: string) =>
  `mission-referrals:v1:${encodeURIComponent(backend.replace(/\/+$/, '').toLowerCase())}:${encodeURIComponent(account)}`;

export function selectMissionReferral(
  records: readonly RememberedMissionReferral[], branchId?: string, periodId?: string, explicitToken?: string, now = Date.now(),
) {
  return records.filter(record => record.branchId === branchId && record.periodMissionConfigurationId === periodId && Date.parse(record.expiresAt) > now)
    .sort((a, b) => Number(b.token === explicitToken) - Number(a.token === explicitToken) || b.openedAt - a.openedAt)[0];
}

export function createMissionReferralRegistry(storage: Storage, now = Date.now) {
  const empty: readonly RememberedMissionReferral[] = [];
  let scope: string | undefined;
  let records: readonly RememberedMissionReferral[] = empty;
  let generation = 0;
  let ready: Promise<void> = Promise.resolve();
  const writes = new Map<string, Promise<unknown>>();
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach(listener => listener());
  const valid = (record: RememberedMissionReferral) => !!record?.referralId && !!record.token && !!record.branchId &&
    !!record.periodMissionConfigurationId && Number.isFinite(record.openedAt) && Date.parse(record.expiresAt) > now();
  const persist = () => {
    if (!scope) return Promise.resolve();
    const key = scope;
    const value = JSON.stringify(records);
    const write = (writes.get(key) ?? Promise.resolve()).catch(() => {}).then(() => storage.setItem(key, value));
    writes.set(key, write);
    // Storage can be unavailable (for example private browsing). Keep this session usable.
    return write.catch(() => {});
  };
  const replace = async (next: readonly RememberedMissionReferral[]) => {
    records = next.filter(valid);
    notify();
    await persist();
  };
  return {
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    snapshot(key?: string) { return key && key === scope ? records : empty; },
    activate(key?: string) {
      if (scope === key) return ready;
      scope = key;
      records = empty;
      const current = ++generation;
      notify();
      ready = (async () => {
        if (!key) return;
        try {
          await writes.get(key)?.catch(() => {});
          const raw = await storage.getItem(key);
          const parsed = raw ? JSON.parse(raw) : [];
          if (generation !== current) return;
          records = Array.isArray(parsed) ? parsed.filter(valid) : empty;
          notify();
          await persist();
        } catch { /* No tokens or storage contents in logs. */ }
      })();
      return ready;
    },
    async remember(key: string, record: RememberedMissionReferral) {
      await ready;
      if (scope !== key || !valid(record)) return false;
      await replace([...records.filter(item => item.referralId !== record.referralId && item.token !== record.token), record]);
      return true;
    },
    async remove(key: string, token: string) {
      await ready;
      if (scope !== key) return;
      await replace(records.filter(record => record.token !== token));
    },
    async update(key: string, token: string, update: Partial<RememberedMissionReferral>) {
      await ready;
      if (scope !== key) return;
      // A refresh finishing after acceptance/removal cannot resurrect an invitation.
      await replace(records.map(record => record.token === token ? { ...record, ...update } : record));
    },
    async prune(key: string) {
      await ready;
      if (scope !== key || records.every(valid)) return;
      await replace(records);
    },
  };
}
